/**
 * PortalSidebar Store - Zustand store with individual selectors
 * Stores sidebar menu data fetched from GET /api/v1/sidebar/menu
 * Following individual selectors pattern (per zustand-patterns.md)
 */

import { create } from 'zustand';
import type { SidebarMenuItemData, SidebarMenuResponse } from '@/application/queries/sidebarMenu';

// ============================================================================
// Store State
// ============================================================================

interface PortalSidebarState {
  // Data
  menuItems: SidebarMenuItemData[];
  logo: string;
  collapsedLogo: string;
  notificationCount: number;
  showNotificationBadge: boolean;
  loading: boolean;
  error: Error | null;

  // Actions
  setData: (data: SidebarMenuResponse) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  reset: () => void;
  _reset: () => void; // For testing
}

const initialState = {
  menuItems: [] as SidebarMenuItemData[],
  logo: '',
  collapsedLogo: '',
  notificationCount: 0,
  showNotificationBadge: false,
  loading: true,
  error: null as Error | null,
};

// ============================================================================
// Store
// ============================================================================

const usePortalSidebarStore = create<PortalSidebarState>((set) => ({
  ...initialState,

  setData: (data) =>
    set({
      menuItems: data.menuItems,
      logo: data.logo,
      collapsedLogo: data.collapsedLogo,
      notificationCount: data.notificationCount,
      showNotificationBadge: data.showNotificationBadge,
      loading: false,
      error: null,
    }),

  setLoading: (loading) => set({ loading }),

  setError: (error) =>
    set({
      error,
      loading: false,
    }),

  reset: () => set({ ...initialState, loading: true }),
  _reset: () => set(initialState),
}));

// ============================================================================
// Individual Selector Hooks (per zustand-patterns.md)
// ============================================================================

// Empty array constant to prevent infinite re-renders
const EMPTY_MENU_ITEMS: SidebarMenuItemData[] = [];

// Data selectors
export const usePortalSidebarMenuItems = () =>
  usePortalSidebarStore((state) =>
    state.menuItems.length > 0 ? state.menuItems : EMPTY_MENU_ITEMS
  );
export const usePortalSidebarLogo = () => usePortalSidebarStore((state) => state.logo);
export const usePortalSidebarCollapsedLogo = () =>
  usePortalSidebarStore((state) => state.collapsedLogo);
export const usePortalSidebarNotificationCount = () =>
  usePortalSidebarStore((state) => state.notificationCount);
export const usePortalSidebarShowBadge = () =>
  usePortalSidebarStore((state) => state.showNotificationBadge);

// State selectors
export const usePortalSidebarLoading = () => usePortalSidebarStore((state) => state.loading);
export const usePortalSidebarError = () => usePortalSidebarStore((state) => state.error);

// Action selectors
export const useSetPortalSidebarData = () => usePortalSidebarStore((state) => state.setData);
export const useSetPortalSidebarLoading = () => usePortalSidebarStore((state) => state.setLoading);
export const useSetPortalSidebarError = () => usePortalSidebarStore((state) => state.setError);
export const useResetPortalSidebar = () => usePortalSidebarStore((state) => state.reset);

// For testing only
export const _resetPortalSidebarStore = () => usePortalSidebarStore.getState()._reset();
